import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import PrescriptionEditor from './PrescriptionEditor';
import PrescriptionList from './PrescriptionList';
import {
  fetchPrescriptions,
  selectPrescriptions,
  selectPrescriptionsError,
  selectPrescriptionsLoading,
  selectPrescriptionsSubmitting,
  submitPrescriptions
} from '../../../ReduxSlices/Prescriptions/prescriptionSlice';
import LoadingDots from '../../shared/LoadingDots';

const PrescriptionSection = ({ patientId, currentUserId, consultationId, patientQueueId }) => {
  const dispatch = useDispatch();
  const prescriptions = useSelector(selectPrescriptions(patientId));
  const loading = useSelector(selectPrescriptionsLoading);
  const error = useSelector(selectPrescriptionsError);
  const submitting = useSelector(selectPrescriptionsSubmitting);

  const [medicationItems, setMedicationItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [deletedMedicationIds, setDeletedMedicationIds] = useState([]);
  

  // Refetch prescriptions when patientId changes
 useEffect(() => {
  if (patientId) {
    dispatch(fetchPrescriptions(patientId));
  }
}, [dispatch, patientId]);

useEffect(() => {
  setMedicationItems([]);
  setEditingId(null);
  handleCancel();
  setDeletedMedicationIds([]);
}, [patientId]);


const handleSubmit = async () => {
  if (!consultationId) {
    console.warn("No consultation ID found.");
    return;
  }
  if (medicationItems.length === 0) {
    console.warn("No medications added.");
    return;
  }

  const payload = {
    patient_id: patientId,
    consultation_id: consultationId,
    patient_queue_id: patientQueueId,
     medication_items_attributes: [
    ...medicationItems,
    ...deletedMedicationIds.map(id => ({ id, _destroy: true }))
  ],
  };

  //console.log('Submitting prescription with patientQueueId:', payload);
  
  try {
    await dispatch(submitPrescriptions({ payload, editingId })).unwrap();
    setMedicationItems([]);
    setEditingId(null);
  } catch (err) {
    console.error("Failed to save prescription:", err);
  }
};


  const handleEdit = (prescription) => {
    setEditingId(prescription.id);
    setMedicationItems(prescription.medication_items || []);
  };

  const handleCancel = () => {
    setEditingId(null);
    setMedicationItems([]);
  };

  return (
    <div className="space-y-4">
      {!consultationId ? (
        <p className="text-sm text-gray-500 italic">No consultation found.</p>
      ) : (
        <PrescriptionEditor
          onSubmit={handleSubmit}
          submitting={submitting}
          onCancel={handleCancel}
          isEditing={!!editingId}
          medicationItems={medicationItems}
          setMedicationItems={setMedicationItems}
          deletedMedicationIds={deletedMedicationIds}
          setDeletedMedicationIds={setDeletedMedicationIds}
        />
      )}

      <hr />

      <h3 className="text-lg font-semibold">Previous Prescriptions</h3>
      <div>
        {loading ? (
          <LoadingDots height={10} width={30} color="#38AD3E" />
        ) : error ? (
        <p className="text-red-500">Error: {error}</p>
        ) : (
          <PrescriptionList
            prescriptions={prescriptions}
            currentUserId={currentUserId}
            onEdit={handleEdit}
          />
        )}
      </div>
    </div>
  );
};

PrescriptionSection.propTypes = {
  patientId: PropTypes.number.isRequired,
  currentUserId: PropTypes.number.isRequired,
  consultationId: PropTypes.number,
  patientQueueId: PropTypes.number,  
};

export default PrescriptionSection;
